import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ApiService } from '../apis/api.service';
import { PageServiceService } from '../page-service.service';
import {
  EmergencyEvacPayload,
  EmergencyType,
  EvacUiState,
} from 'src/app/_models/emergency.models';

const INITIAL_STATE: EvacUiState = {
  showProgress: false,
  isEvacMessageSent: false,
  isDrillEvac: false,
  enableEvacButton: false,
  enableDrillButton: false,
  showEvacError: false,
  showDrillError: false,
};

const MIN_REASON_LENGTH = 4;

/**
 * Scoped to the UpdateWidgetComponent lifecycle via `providers` array.
 * Manages emergency evacuation state and API interactions.
 */
@Injectable()
export class EmergencyEvacuationService implements OnDestroy {
  private stateSubject = new BehaviorSubject<EvacUiState>({ ...INITIAL_STATE });
  readonly state$: Observable<EvacUiState> = this.stateSubject.asObservable();

  private hotelInventoryId = '';
  private subscriptions = new Subscription();

  constructor(
    private apiService: ApiService,
    private pageService: PageServiceService,
  ) {
    // BehaviorSubject emits immediately — synchronous assignment is safe here.
    this.subscriptions.add(
      this.pageService.getCurrentHotelInventoryId().subscribe((id) => {
        this.hotelInventoryId = id;
      }),
    );
  }

  /**
   * This service is provided per-component (see UpdateWidgetComponent providers),
   * so Angular calls this when the host component is destroyed. Without it every
   * widget instance would stay alive via the PageService BehaviorSubject.
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Validates the reason text and updates button/error state accordingly.
   * Called on every field value change from the form.
   */
  validateReason(reason: string, type: EmergencyType): void {
    const isValid = reason.length >= MIN_REASON_LENGTH;
    if (type === 'evacuation') {
      this.patchState({ enableEvacButton: isValid, showEvacError: !isValid });
    } else {
      this.patchState({ enableDrillButton: isValid, showDrillError: !isValid });
    }
  }

  sendNotification(type: EmergencyType, reason: string): void {
    const payload: EmergencyEvacPayload = {
      inventoryStoreId: this.hotelInventoryId,
      emergencyNotificationType: type === 'evacuation' ? 'EVACUATION' : 'EVACUATION_DRILL',
      reason,
    };

    this.patchState({ showProgress: true, isEvacMessageSent: false, isDrillEvac: type === 'drill' });

    this.apiService.getEmergencyNotification(payload).subscribe({
      next: () => this.patchState({ showProgress: false, isEvacMessageSent: true }),
      error: () => this.patchState({ showProgress: false }),
    });
  }

  private patchState(partial: Partial<EvacUiState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }
}
